import { Modal, Typography } from "@drone-js/core";
import { useMutation } from "@tanstack/react-query";
import { oneOnboardingApi } from "libs/api";
import type { FunctionComponent } from "react";
import { useParams } from "router";
import { NotificationType } from "../../notification/constants";
import { useNotificationHandler } from "../../notification/useNotificationHandler";
import { useStaticDataAtomValue } from "../../staticDataAtom";

interface Props {
    photoId: number;
    show: boolean;
    onCancel: () => void;
    onRemove: () => void;
}

export const RemovePhotoModal: FunctionComponent<Props> = ({ photoId, show, onCancel, onRemove }) => {
    const { lang, propertyId } = useParams("/:lang/list-my-property/onboard/:propertyId/photos");
    const {
        cmsData: {
            photo: { cancel, delete: del, deletePhoto, areYouSureYouWantToDeleteThisPhoto },
        },
    } = useStaticDataAtomValue();
    const { addNotification } = useNotificationHandler();
    const { mutate, isLoading } = useMutation({
        mutationFn: (id: number) =>
            oneOnboardingApi.deletePhoto(lang, id, Number(propertyId)).then((res) => {
                if (res.data) return res.data;
                else throw new Error("Failed to delete photo");
            }),
        onSuccess: () => {
            addNotification(NotificationType.DeleteSuccess);
            onRemove();
        },
        onError: () => {
            addNotification(NotificationType.DeleteFailed);
            onCancel();
        },
    });

    return (
        <Modal
            visible={show}
            title={deletePhoto}
            actions={[
                {
                    label: del,
                    hierarchy: "primary",
                    actionType: "button",
                    variant: "label",
                    state: isLoading ? "loading" : "enabled",
                    onClick: () => mutate(photoId),
                    "data-element-name": "confirm-delete-photo",
                },
                {
                    label: cancel,
                    hierarchy: "secondary",
                    actionType: "button",
                    variant: "label",
                    state: isLoading ? "disabled" : "enabled",
                    onClick: onCancel,
                },
            ]}
            onClose={onCancel}
        >
            <Typography variant="body-s">{areYouSureYouWantToDeleteThisPhoto}</Typography>
        </Modal>
    );
};
